import { visibleCarCaps } from "../../design/productConfig";
import type { PerformanceTier, RemoteDriver } from "../../types/traffic";

export function remoteStaleThresholdMs(
  heartbeatIntervalSeconds: number,
): number {
  return Math.max(45_000, Math.max(1, heartbeatIntervalSeconds) * 2_500);
}

export function inferSessionDistanceOrigin(
  authoritativeDistanceMiles: number,
  submittedDistanceMiles: number,
): number {
  return Math.max(
    0,
    Math.max(0, authoritativeDistanceMiles) -
      Math.max(0, submittedDistanceMiles),
  );
}

export function reconcileSessionDistance(
  authoritativeDistanceMiles: number,
  requestDistanceMiles: number,
  currentDistanceMiles: number,
  sessionDistanceBaseMiles = 0,
): number {
  const accruedDuringRequest = Math.max(
    0,
    currentDistanceMiles - requestDistanceMiles,
  );
  return (
    Math.max(0, sessionDistanceBaseMiles) +
    Math.max(0, authoritativeDistanceMiles) +
    accruedDuringRequest
  );
}

export function limitRemoteDrivers(
  drivers: RemoteDriver[],
  tier: PerformanceTier,
): RemoteDriver[] {
  const cap = visibleCarCaps[tier];
  if (drivers.length <= cap) return drivers;
  return [...drivers]
    .sort((left, right) => left.sessionId.localeCompare(right.sessionId))
    .slice(0, cap);
}

export function excludeLocalDriver(
  drivers: RemoteDriver[],
  localSessionId?: string | null,
  localPublicDriverId?: string | null,
): RemoteDriver[] {
  return drivers.filter(
    (driver) =>
      driver.sessionId !== localSessionId &&
      driver.publicDriverId !== localPublicDriverId,
  );
}

export function dedupeRemoteDrivers(drivers: RemoteDriver[]): RemoteDriver[] {
  const byDriver = new Map<string, RemoteDriver>();
  for (const driver of drivers) {
    const existing = byDriver.get(driver.publicDriverId);
    if (!existing || preferRemoteDriver(driver, existing)) {
      byDriver.set(driver.publicDriverId, driver);
    }
  }
  return [...byDriver.values()];
}

export function syncSelectedRemote(
  selected: RemoteDriver | null,
  drivers: RemoteDriver[],
): RemoteDriver | null {
  if (!selected) return null;
  return (
    drivers.find((driver) => driver.sessionId === selected.sessionId) ??
    drivers.find(
      (driver) => driver.publicDriverId === selected.publicDriverId,
    ) ??
    null
  );
}

export function stampRemotePresence(
  driver: RemoteDriver,
  receivedAt = Date.now(),
): RemoteDriver {
  return {
    ...driver,
    presenceReceivedAt: receivedAt,
    departing: driver.departing === true,
  };
}

export function isSessionBlocked(
  sessionId: string,
  departedUntil: Map<string, number>,
  now = Date.now(),
): boolean {
  return (departedUntil.get(sessionId) ?? 0) > now;
}

export function remoteIdsMissingFromLiveSet(
  drivers: RemoteDriver[],
  liveSessionIds: readonly string[],
): string[] {
  const live = new Set(liveSessionIds);
  return drivers
    .filter((driver) => !driver.departing && !live.has(driver.sessionId))
    .map((driver) => driver.sessionId);
}

export function prepareRemoteSnapshot(input: {
  current: RemoteDriver[];
  incoming: RemoteDriver[];
  localSessionId?: string | null;
  localPublicDriverId?: string | null;
  departedUntil: Map<string, number>;
  now?: number;
  performanceTier: PerformanceTier;
}): {
  remoteDrivers: RemoteDriver[];
  missingSessionIds: string[];
  revivedSessionIds: string[];
} {
  const now = input.now ?? Date.now();
  const cleaned = dedupeRemoteDrivers(
    excludeLocalDriver(
      input.incoming.filter(
        (driver) =>
          !isSessionBlocked(driver.sessionId, input.departedUntil, now),
      ),
      input.localSessionId,
      input.localPublicDriverId,
    ).map((driver) => ({ ...driver, departing: false })),
  );
  const incomingIds = new Set(cleaned.map((driver) => driver.sessionId));
  const incomingDriverIds = new Set(
    cleaned.map((driver) => driver.publicDriverId),
  );
  const missingSessionIds: string[] = [];
  const lingering = input.current.filter((driver) => {
    if (incomingIds.has(driver.sessionId)) return false;
    if (incomingDriverIds.has(driver.publicDriverId)) return false;
    missingSessionIds.push(driver.sessionId);
    return true;
  });
  return {
    remoteDrivers: limitRemoteDrivers(
      dedupeRemoteDrivers([...cleaned, ...lingering]),
      input.performanceTier,
    ),
    missingSessionIds,
    revivedSessionIds: cleaned.map((driver) => driver.sessionId),
  };
}

function preferRemoteDriver(
  candidate: RemoteDriver,
  existing: RemoteDriver,
): boolean {
  const candidateDeparting = candidate.departing === true;
  const existingDeparting = existing.departing === true;
  if (existingDeparting !== candidateDeparting) return existingDeparting;
  return Date.parse(candidate.joinedAt) >= Date.parse(existing.joinedAt);
}
